<template>
  <form @submit.prevent>
    <section id="information">
      <template v-if="hasAccount">
        <button
          class="link float-right margin-0 padding-y-4xs padding-x-0"
          aria-label="Sign out of this account"
          data-balloon-pos="left"
          @click="beginSignOut"
        >
          Sign Out
        </button>

        <dl style="--padding-left: 0">
          <dt class="sr-only">Current Account</dt>
          <dd style="font-weight: var(--font-weight-semibold)">
            <span
              v-if="hasExistingName"
              class="display-block margin-bottom-4xs"
            >
              {{ existingFirstName }} {{ existingLastName }}
            </span>
            <span class="size-lg">{{ existingEmail }}</span>
          </dd>
        </dl>
      </template>

      <template v-else>
        <wa-message
          v-if="isShowingEmailExistsNotice"
          theme="blue"
          title="You've already got an account!"
          type="flash"
        >
          <p slot="content">
            <site-link
              :to="{ name: 'sessions.sign-in' }"
              @click.native="$posthog.capture('sign_in_button_click', { context: 'purchase_form' })"
              >Sign in</site-link
            >
            to continue or perhaps you need to use a different email address?
          </p>
        </wa-message>

        <wa-message
          v-if="$invalid({ after: 'payment', topics: ['email'] })"
          theme="danger"
          content="Email address is not valid"
          type="flash"
        />

        <site-link
          :to="{ name: 'sessions.sign-in', query: { next: $route.path } }"
          class="secondary float-right"
          @click.native="$posthog.capture('sign_in_button_click', { context: 'purchase_form' })"
        >
          Already have an account?
        </site-link>

        <wa-text-field
          ref="accountEmail"
          v-model="email"
          :disabled="!!isPlacingOrder"
          :is-processing="isCheckingEmailAvailability"
          :is-valid="$valid({ after: 'payment', topics: ['email'] }) && !errors.hasError('email')"
          has-icon
          icon-placement="before"
          icon-style="regular"
          icon-name="faEnvelope"
          is-required
          label="Email Address"
          placeholder="e.g. tchalla@wakanda.gov"
          type="email"
        />

        <wa-message
          v-if="$invalid({ after: 'payment', topics: ['password'] })"
          content="Password is required"
          theme="danger"
          type="flash"
        />

        <wa-message
          v-if="$invalid({ after: 'payment', topics: ['passwordConfirmation'] })"
          content="Password confirmation is required"
          theme="danger"
          type="flash"
        />

        <wa-message
          v-if="$invalid({ after: 'payment', topics: ['passwordsMatch'] })"
          content="Give it another try and make sure to enter the same phrase."
          title="Those passwords do not match."
          theme="danger"
          type="flash"
        />

        <wa-message
          v-if="$invalid({ after: 'payment', topics: ['passwordLength'] })"
          content="Give it another try and don't just add a bunch of 1's at the end."
          title="This password is a bit too short."
          theme="danger"
          type="flash"
        />

        <div class="row roomy align-between">
          <div class="column">
            <wa-text-field
              v-model="password"
              :disabled="!!isPlacingOrder"
              :valid="$valid({ after: 'payment', topics: ['password', 'passwordsMatch', 'passwordLength'] }) && !errors.hasError('password')"
              has-icon
              icon-style="regular"
              icon-name="faLock"
              is-required
              label="Password"
              tip="Make it a good one!"
              type="password"
            />
          </div>
          <div class="column">
            <wa-text-field
              v-model="passwordConfirmation"
              :disabled="!!isPlacingOrder"
              :valid="
                $valid({ after: 'payment', topics: ['passwordConfirmation', 'passwordsMatch', 'passwordLength'] }) && !errors.hasError('passwordConfirmation')
              "
              has-icon
              icon-style="regular"
              icon-name="faLock"
              is-required
              label="Confirm Password"
              tip="Make it match the good one!"
              type="password"
            />
          </div>
        </div>
      </template>
    </section>

    <hr class="margin-y-4xl" />

    <template v-for="(message, index) in errors.genericMessages">
      <wa-message
        :key="index"
        title="Sorry, we couldn't process your order!"
        theme="danger"
        type="flash"
      >
        <p slot="content">
          {{ message || '' }} If the problem persists,
          <a
            href="mailto:hello@fontawesome.com?subject=I can't complete a payment, Font Awesome"
            @click="supportLinkClicked('purchase_form_fail')"
            >let us know</a
          >
          and we'll get you unstuck.
        </p>
      </wa-message>
    </template>

    <payment
      ref="payment"
      v-model="selectedPaymentMethod"
      :options="paymentOptions"
      :button-enabled="isReadyForPayment"
      :button-processing="!!isPlacingOrder"
      :is-processing="!!isPlacingOrder"
      :total-in-cents="totalCents"
      :item-label="billingDescription"
      label="Font Awesome Pro"
      :theme="theme"
      @button-clicked="handleButtonClicked"
      @token="handlePaymentToken"
      @error="handlePaymentError"
    />

    <wa-media v-model="sizes" />
  </form>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import { get, throttle } from 'lodash'
  import { flow, trim, isEmpty } from 'lodash/fp'
  import FormErrors from 'app/utils/form_errors'
  import transactions from 'app/utils/mixins/transactions'
  import Payment from 'app/views/buy/components/Payment'
  import { EventTrackingMixin } from 'app/mixins'

  const EMAIL_EXISTS_THROTTLE_WAIT = 1000 // in ms

  const name = 'PurchaseForm'
  const empty = flow(trim, isEmpty)

  export default {
    name,

    components: {
      Payment
    },

    mixins: [transactions, EventTrackingMixin],

    props: {
      theme: {
        type: String,
        required: true
      },

      billingDescription: {
        type: String,
        required: true
      },

      currentProduct: {
        type: Object,
        required: true
      },

      totalCents: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        isPlacingOrder: false,
        errors: new FormErrors(),
        selectedPaymentMethod: 'card',
        paymentOptions: ['card'],
        emailAvailable: true,
        sizes: new Set(),
        isCheckingEmailAvailability: false,
        newCreatedAccount: false,

        email: {
          valid: true,
          value: ''
        },

        password: {
          valid: true,
          value: ''
        },

        passwordConfirmation: {
          valid: true,
          value: ''
        }
      }
    },

    validation: {
      refs: {
        payment: 'button-clicked'
      },

      topics: {
        passwordsMatch() {
          return this.password.value === this.passwordConfirmation.value
        },

        passwordLength() {
          return this.password.value.length >= 6
        }
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),

      existingEmail() {
        return get(this, ['$account', 'email'], '')
      },

      existingFirstName() {
        return get(this, ['$profile', 'firstName'], '')
      },

      existingLastName() {
        return get(this, ['$profile', 'lastName'], '')
      },

      hasAccount() {
        return !!this.$account
      },

      hasExistingName() {
        return !!this.existingFirstName && !!this.existingLastName
      },

      hasExistingSubscription() {
        return !!this.currentSubscription
      },

      isEmailEmpty() {
        return empty(this.email.value)
      },

      isPasswordEmpty() {
        return empty(this.password.value)
      },

      isPasswordConfirmationEmpty() {
        return empty(this.passwordConfirmation.value)
      },

      isNotReadyForPayment() {
        const missingData = this.isEmailEmpty || this.isPasswordEmpty || this.isPasswordConfirmationEmpty

        if (this.hasAccount) {
          return false
        } else {
          return missingData || !this.emailAvailable
        }
      },

      isReadyForPayment() {
        return !this.isNotReadyForPayment
      },

      isShowingEmailExistsNotice() {
        return !this.hasAccount && !this.emailAvailable
      }
    },

    watch: {
      email() {
        const { valid, value } = this.email

        if (valid && !this.hasAccount) {
          this.validateEmail(value)
        } else {
          this.emailAvailable = true
        }
      }
    },

    methods: {
      handleButtonClicked(event) {
        const ref = 'payment'

        this.$validate({ throughTrigger: ref })

        const valid = this.hasAccount || this.$valid({ after: ref, topics: Object.keys(this.$validations) })

        if (!valid) {
          event.preventDefault()
        }
      },

      ...mapActions('accountTenancy', ['emailExists']),

      ...mapActions('sessions', ['signOut']),

      ...mapActions('orders', ['createOrderWithAccount', 'createOrderWithoutAccount']),

      async beginSignOut() {
        await this.signOut()
        this.$refs.accountEmail.value.value = ''
      },

      isValid() {
        return this.$valid({
          topics: ['email', 'password', 'passwordConfirmation', 'passwordsMatch', 'passwordLength']
        })
      },

      validateEmail: throttle(async function (email) {
        this.isCheckingEmailAvailability = true

        try {
          this.emailAvailable = !(await this.emailExists({ email }))
        } finally {
          this.isCheckingEmailAvailability = false
        }
      }, EMAIL_EXISTS_THROTTLE_WAIT),

      async handlePaymentToken({ token, successCallback, failureCallback }) {
        if ((this.isValid() || this.hasAccount) && token) {
          try {
            await this.processPayment(token)

            successCallback()
          } catch (errors) {
            this.errors = errors

            failureCallback()
          }
        }
      },

      handlePaymentError(errors) {
        this.errors = errors
      },

      async processPayment(token) {
        this.isPlacingOrder = this.selectedPaymentMethod

        // map UI payment method options to processor types as we refer
        // to them on the server
        const processor = ((method) => {
          const val = 'stripe'

          if (method === 'paypal') {
            return 'braintree'
          }

          return val
        })(this.selectedPaymentMethod)

        try {
          this.$emit('is-purchasing')

          await this.purchaseCommission({
            processor,
            processorToken: token.id,
            email: this.email.value,
            password: this.password.value
          })

          this.$router.push({ name: 'commission.buy', query: { new: this.newCreatedAccount } })
        } catch (errors) {
          this.isPlacingOrder = false

          if (errors instanceof FormErrors) {
            this.errors = errors
          } else {
            this.errors = new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat.' }])
          }
        }
      },

      async purchaseCommission(data) {
        const { processorToken } = data

        if (this.hasAccount) {
          this.createOrderWithAccount({
            sku: this.currentProduct.sku,
            token: processorToken,
            ...data
          })
        } else {
          this.createOrderWithoutAccount({
            email: this.email.value,
            password: this.password.value,
            sku: this.currentProduct.sku,
            token: processorToken,
            ...data
          })

          this.newCreatedAccount = true
        }
      }
    }
  }
</script>
